import axios from '../../../configs/axios';

export type WebinarSource = 'youtube' | 'zoom' | 'vimeo' | 'iframe';
export type WebinarStatus = 'upcoming' | 'recorded';

export interface WebinarResource {
  title: string;
  url: string;
  type: 'file' | 'link';
  fileKey?: string; // S3 key for uploaded files (used for presigned download)
  fileType?: string;
  fileSize?: string;
}

export interface Webinar {
  id: string;
  title: string;
  description?: string;
  videoUrl: string;
  resources?: WebinarResource[];
  source: WebinarSource;
  status: WebinarStatus;
  eventDate?: string;
  duration?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebinarRequest {
  title: string;
  description?: string;
  videoUrl: string;
  source: WebinarSource;
  status: WebinarStatus;
  eventDate?: string;
  duration?: string;
  resources?: WebinarResource[];
}

export interface UpdateWebinarRequest extends Partial<CreateWebinarRequest> {}

export const webinarsAPI = {
  getAllWebinars: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await axios.get('/admin/webinars', { params });
    return response.data;
  },

  getWebinarById: async (id: string) => {
    const response = await axios.get(`/admin/webinars/${id}`);
    return response.data;
  },

  createWebinar: async (data: CreateWebinarRequest) => {
    const response = await axios.post('/admin/webinars', data);
    return response.data;
  },

  updateWebinar: async (id: string, data: UpdateWebinarRequest) => {
    const response = await axios.put(`/admin/webinars/${id}`, data);
    return response.data;
  },

  deleteWebinar: async (id: string) => {
    const response = await axios.delete(`/admin/webinars/${id}`);
    return response.data;
  },

  // Upload a resource file to S3 and return its public URL + metadata.
  uploadResourceFile: async (file: File, webinarTitle: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'webinar-resources');
    formData.append('name', webinarTitle || 'resource');
    formData.append('version', '1');

    const response = await axios.post('/s3/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Backend returns { fileUrl, fileName, fileKey, fileSize, fileType }
    return response.data.data as {
      fileUrl: string;
      fileName: string;
      fileKey: string;
      fileSize: string;
      fileType: string;
    };
  },
};

/** Extract the YouTube video id from any common YouTube URL form. */
export const getYoutubeId = (url: string): string | null => {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
  );
  return match?.[1] ?? null;
};

/**
 * Best-effort thumbnail image for a webinar card.
 * - YouTube -> hqdefault image
 * - Vimeo -> not derivable without an API call, so returns null (icon fallback)
 * - Zoom / other -> null (icon fallback)
 */
export const getThumbnailUrl = (
  url: string,
  source: WebinarSource
): string | null => {
  if (source === 'youtube' || /youtu\.?be/.test(url)) {
    const id = getYoutubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  }
  return null;
};
